import { UserManager, WebStorageStateStore } from 'oidc-client';
import { apiUrl } from '../config';

/** Returns the public SSO config or { enabled: false } if not configured */
export async function fetchSsoConfig(databaseName) {
    try {
        const res = await fetch(`${apiUrl}/ManagerReport/SsoConfig/${encodeURIComponent(databaseName)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Database-Name': databaseName
            }
        });

        if (!res.ok) return { enabled: false };
        return await res.json();
    } catch (err) {
        console.error('Failed to fetch SSO config:', err);
        return { enabled: false };
    }
}

/** Redirects the browser to the Okta authorisation page using PKCE */
export function startSsoLogin(ssoConfig, databaseName) {
    const redirectUri = `${window.location.origin}/${encodeURIComponent(databaseName)}/sso-callback`;

    const userManager = new UserManager({
        authority: ssoConfig.authority,
        client_id: ssoConfig.clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: ssoConfig.scopes || 'openid profile email',
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
        stateStore: new WebStorageStateStore({ store: window.sessionStorage })
    });

    sessionStorage.setItem('sso_database_name', databaseName);
    return userManager.signinRedirect();
}

/** Handles the SSO callback: exchanges the authorisation code for tokens via the backend */
export async function handleSsoCallback(databaseName) {
    const redirectUri = `${window.location.origin}/${encodeURIComponent(databaseName)}/sso-callback`;

    // Placeholder values — authority/client_id are not used during callback processing
    const userManager = new UserManager({
        authority: '_',
        client_id: '_',
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid profile email',
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
        stateStore: new WebStorageStateStore({ store: window.sessionStorage })
    });

    await userManager.signinRedirectCallback();

    // Retrieve PKCE code_verifier and nonce from oidc-client's sessionStorage state
    let codeVerifier = null;
    let nonce = null;

    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('oidc.')) {
            try {
                const stored = JSON.parse(sessionStorage.getItem(key));
                if (stored && stored.code_verifier) {
                    codeVerifier = stored.code_verifier;
                    nonce = stored.nonce;
                    break;
                }
            } catch (e) {
                // Skip non-JSON entries
            }
        }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (!code) {
        throw new Error('No authorisation code found in the callback URL.');
    }

    const res = await fetch(`${apiUrl}/ManagerReport/SsoExchange`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Database-Name': databaseName
        },
        body: JSON.stringify({ code, codeVerifier, nonce, redirectUri, databaseName })
    });

    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'SSO authentication failed.');
    }

    return await res.json();
}
